'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@morpho-org/ui-v6/utils';
import { Icon, Toggletip, ToggletipContent, ToggletipTrigger } from '@repo/ui';
import { Info } from '@repo/ui/icons';
import { Slider as RadixSlider } from 'radix-ui';
import { use, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import type { RangeValue } from './MasterFilterRange';

// Inlined from the (removed) Vaults app Slider CVA — this raw-radix range slider
// borrowed its styling. Classes are the non-disabled variant, verbatim.
const SLIDER_ROOT = 'relative flex h-[18px] w-full touch-none items-center select-none cursor-default';
const SLIDER_TRACK = 'bg-muted relative h-[10px] grow overflow-hidden rounded-full cursor-pointer';
const SLIDER_THUMB = 'bg-primary-foreground shadow-sm block size-[18px] rounded-full transition-transform duration-200 ml-px focus:outline-none cursor-grab hover:scale-[1.2] active:cursor-grabbing';
import type { MasterFilterState } from './types';
import { MasterFilterContext } from './MasterFilter';
export function isRangeValue(value: unknown): value is RangeValue {
  if (typeof value !== 'object' || value === null) return false;
  const keys = Object.keys(value);
  return keys.every(k => k === 'min' || k === 'max');
}
export function valueToIndex(val: number, ticks: RangeSliderTick[]): number {
  let bestIdx = 0;
  let bestDist = Math.abs(ticks[0]!.value - val);
  for (let i = 1; i < ticks.length; i++) {
    const dist = Math.abs(ticks[i]!.value - val);
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  }
  return bestIdx;
}
export interface RangeSliderTick {
  value: number;
  label: string;
}
export interface MasterFilterRangeSliderProps<TState extends MasterFilterState = MasterFilterState> {
  name: Extract<keyof TState, string>;
  label: string;
  tooltip?: string;
  min?: number;
  max?: number;
  step?: number;
  ticks?: RangeSliderTick[];
  snapToTicks?: boolean;
  debounceMs?: number;
  className?: string;
}
export function MasterFilterRangeSlider(t0) {
  const $ = _c(72);
  const {
    name,
    label,
    tooltip,
    min: t1,
    max: t2,
    step: t3,
    ticks,
    snapToTicks,
    debounceMs: t4,
    className
  } = t0;
  const min = t1 === undefined ? 0 : t1;
  const max = t2 === undefined ? 100 : t2;
  const step = t3 === undefined ? 0.1 : t3;
  const debounceMs = t4 === undefined ? 500 : t4;
  const context = use(MasterFilterContext);
  if (!context) {
    throw new Error("MasterFilter.RangeSlider must be used within a MasterFilter.Provider");
  }
  const {
    state,
    actions
  } = context;
  let t5;
  if ($[0] !== name || $[1] !== state) {
    t5 = isRangeValue(state[name]) ? state[name] : {};
    $[0] = name;
    $[1] = state;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const range = t5;
  const isDiscrete = snapToTicks && ticks && ticks.length > 0;
  const sliderMin = isDiscrete ? 0 : min;
  const sliderMax = isDiscrete ? ticks.length - 1 : max;
  const sliderStep = isDiscrete ? 1 : step;
  let t6;
  if ($[3] !== isDiscrete || $[4] !== ticks) {
    t6 = val => isDiscrete ? valueToIndex(val, ticks) : val;
    $[3] = isDiscrete;
    $[4] = ticks;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const toSliderValue = t6;
  let t7;
  if ($[6] !== isDiscrete || $[7] !== ticks) {
    t7 = idx => isDiscrete ? ticks[idx].value : idx;
    $[6] = isDiscrete;
    $[7] = ticks;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const fromSliderValue = t7;
  const defaultMin = isDiscrete ? ticks[0].value : min;
  let t8;
  if ($[9] !== isDiscrete || $[10] !== max || $[11] !== ticks) {
    t8 = isDiscrete ? ticks.at(-1).value : max;
    $[9] = isDiscrete;
    $[10] = max;
    $[11] = ticks;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const defaultMax = t8;
  let t9;
  if ($[13] !== defaultMax || $[14] !== defaultMin || $[15] !== range.max || $[16] !== range.min || $[17] !== toSliderValue) {
    t9 = () => [toSliderValue(range.min ?? defaultMin), toSliderValue(range.max ?? defaultMax)];
    $[13] = defaultMax;
    $[14] = defaultMin;
    $[15] = range.max;
    $[16] = range.min;
    $[17] = toSliderValue;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const [localValues, setLocalValues] = useState(t9);
  const pendingRangeRef = useRef(range);
  const [prevRange, setPrevRange] = useState(range);
  if (prevRange.min !== range.min || prevRange.max !== range.max) {
    setPrevRange(range);
    setLocalValues([toSliderValue(range.min ?? defaultMin), toSliderValue(range.max ?? defaultMax)]);
  }
  let t10;
  let t11;
  if ($[19] !== range.max || $[20] !== range.min) {
    t10 = () => {
      pendingRangeRef.current = {
        min: range.min,
        max: range.max
      };
    };
    t11 = [range.min, range.max];
    $[19] = range.max;
    $[20] = range.min;
    $[21] = t10;
    $[22] = t11;
  } else {
    t10 = $[21];
    t11 = $[22];
  }
  useLayoutEffect(t10, t11);
  let t12;
  if ($[23] !== actions || $[24] !== name) {
    t12 = () => {
      actions.updateField(name, pendingRangeRef.current);
    };
    $[23] = actions;
    $[24] = name;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const debouncedFlush = useDebouncedCallback(t12, debounceMs);
  let t13;
  let t14;
  if ($[26] !== debouncedFlush) {
    t13 = () => () => {
      debouncedFlush.flush();
    };
    t14 = [debouncedFlush];
    $[26] = debouncedFlush;
    $[27] = t13;
    $[28] = t14;
  } else {
    t13 = $[27];
    t14 = $[28];
  }
  useEffect(t13, t14);
  let t15;
  if ($[29] !== actions || $[30] !== debounceMs || $[31] !== debouncedFlush || $[32] !== fromSliderValue || $[33] !== isDiscrete || $[34] !== max || $[35] !== min || $[36] !== name || $[37] !== ticks) {
    t15 = t16 => {
      const [newMinSlider, newMaxSlider] = t16;
      if (newMinSlider === undefined || newMaxSlider === undefined) {
        return;
      }
      setLocalValues([newMinSlider, newMaxSlider]);
      const newMinActual = fromSliderValue(newMinSlider);
      const newMaxActual = fromSliderValue(newMaxSlider);
      const newRange = {};
      if (isDiscrete) {
        if (newMinSlider !== 0) {
          newRange.min = newMinActual;
        }
        if (newMaxSlider !== ticks.length - 1) {
          newRange.max = newMaxActual;
        }
      } else {
        if (newMinActual !== min) {
          newRange.min = newMinActual;
        }
        if (newMaxActual !== max) {
          newRange.max = newMaxActual;
        }
      }
      pendingRangeRef.current = newRange;
      if (debounceMs > 0) {
        debouncedFlush();
      } else {
        actions.updateField(name, newRange);
      }
    };
    $[29] = actions;
    $[30] = debounceMs;
    $[31] = debouncedFlush;
    $[32] = fromSliderValue;
    $[33] = isDiscrete;
    $[34] = max;
    $[35] = min;
    $[36] = name;
    $[37] = ticks;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const handleValueChange = t15;
  let t16;
  if ($[39] !== className) {
    t16 = cn("gap-2xs py-2xs flex flex-col", className);
    $[39] = className;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== label) {
    t17 = <span className="text-2xs text-foreground">{label}</span>;
    $[41] = label;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== tooltip) {
    t18 = tooltip && <Toggletip><ToggletipTrigger asChild={true}><span className="cursor-help" role="button" tabIndex={0} aria-label="More information"><Icon icon={<Info />} className="text-muted-foreground hover:text-secondary-foreground" /></span></ToggletipTrigger><ToggletipContent className="z-60">{tooltip}</ToggletipContent></Toggletip>;
    $[43] = tooltip;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t17 || $[46] !== t18) {
    t19 = <div className="gap-2xs flex items-center">{t17}{t18}</div>;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = cn(SLIDER_ROOT, "mt-xs");
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = cn(SLIDER_TRACK);
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <RadixSlider.Track className={t21}><RadixSlider.Range className={cn("bg-info-foreground absolute h-full rounded-full")} /></RadixSlider.Track>;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  const t23 = `${label} minimum`;
  let t24;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = cn(SLIDER_THUMB);
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== t23) {
    t25 = <RadixSlider.Thumb aria-label={t23} className={t24} />;
    $[52] = t23;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  const t26 = `${label} maximum`;
  let t27;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = cn(SLIDER_THUMB);
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  let t28;
  if ($[55] !== t26) {
    t28 = <RadixSlider.Thumb aria-label={t26} className={t27} />;
    $[55] = t26;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  let t29;
  if ($[57] !== handleValueChange || $[58] !== localValues || $[59] !== sliderMax || $[60] !== sliderMin || $[61] !== sliderStep || $[62] !== t25 || $[63] !== t28) {
    t29 = <RadixSlider.Root className={t20} value={localValues} min={sliderMin} max={sliderMax} step={sliderStep} onValueChange={handleValueChange} minStepsBetweenThumbs={0}>{t22}{t25}{t28}</RadixSlider.Root>;
    $[57] = handleValueChange;
    $[58] = localValues;
    $[59] = sliderMax;
    $[60] = sliderMin;
    $[61] = sliderStep;
    $[62] = t25;
    $[63] = t28;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  if ($[65] !== ticks) {
    t30 = ticks && ticks.length > 0 && <div className="mt-2xs flex justify-between">{ticks.map(_temp)}</div>;
    $[65] = ticks;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== t16 || $[68] !== t19 || $[69] !== t29 || $[70] !== t30) {
    t31 = <div className={t16}>{t19}{t29}{t30}</div>;
    $[67] = t16;
    $[68] = t19;
    $[69] = t29;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  return t31;
}
function _temp(tick) {
  return <span key={tick.value} className="text-3xs text-muted-foreground">{tick.label}</span>;
}
MasterFilterRangeSlider.displayName = 'MasterFilterRangeSlider';